// src/components/auth/LoginForm.tsx
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../stores/useAuthStore';
import {
  handleLogin,
  handleGoogleSignIn,
  handleGoogleError,
  getAuthErrorMessage,
} from '../../services/authService';

const C = {
  surface: '#0d1220',
  inputBg: 'rgba(255,255,255,0.04)',
  border:  'rgba(255,255,255,0.08)',
  accent:  '#4f8cf7',
  accent2: '#6366f1',
  success: '#34d399',
  error:   '#f87171',
  text:    '#f1f5f9',
  muted:   '#8b9cc4',
};

interface FormValues { email: string; password: string; }
interface MessageState { text: string; type: 'error' | 'success' | 'info'; }

function GoogleIcon(): React.ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

const msgStyle = (type: string): React.CSSProperties => ({
  padding:         '13px 16px',
  borderRadius:    '12px',
  fontSize:        '0.85rem',
  lineHeight:      1.5,
  background:
    type === 'error'   ? 'rgba(248,113,113,0.08)' :
    type === 'success' ? 'rgba(52,211,153,0.08)'  :
                         'rgba(79,140,247,0.08)',
  color:
    type === 'error'   ? C.error   :
    type === 'success' ? C.success :
                         C.accent,
  border: `1px solid ${
    type === 'error'   ? 'rgba(248,113,113,0.25)' :
    type === 'success' ? 'rgba(52,211,153,0.25)'  :
                         'rgba(79,140,247,0.25)'}`,
});

export function LoginForm(): React.ReactElement {
  const closeModal = useAuthStore((s) => s.closeAuthModal);
  const setTab     = useAuthStore((s) => s.setAuthTab);

  const [message, setMessage]               = useState<MessageState | null>(null);
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [emailFocus, setEmailFocus]         = useState(false);
  const [pwFocus, setPwFocus]               = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormValues>();
  const showMsg = (text: string, type: MessageState['type']) => setMessage({ text, type });

  const inputStyle = (focused: boolean, hasError?: boolean): React.CSSProperties => ({
    width:           '100%',
    padding:         '13px 16px',
    background:      'rgba(255,255,255,0.04)',
    border:          `1px solid ${focused ? C.accent : hasError ? C.error : 'rgba(255,255,255,0.08)'}`,
    borderRadius:    '12px',
    color:           C.text,
    fontSize:        '0.95rem',
    outline:         'none',
    boxSizing:       'border-box',
    transition:      'border-color 0.2s ease, box-shadow 0.2s ease',
    boxShadow:       focused ? `0 0 0 3px rgba(79,140,247,0.12)` : 'none',
    fontFamily:      'inherit',
  });

  const labelStyle: React.CSSProperties = {
    fontSize:      '0.72rem',
    fontWeight:    700,
    color:         C.muted,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom:  '8px',
    display:       'block',
  };

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    setMessage(null);
    try {
      await handleLogin(values.email, values.password);
      showMsg('Welcome back!', 'success');
      setTimeout(() => closeModal(), 800);
    } catch (err) {
      showMsg(getAuthErrorMessage(err), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onGoogle = async () => {
    setIsGoogleLoading(true);
    setMessage(null);
    try {
      const { isNewUser, displayName } = await handleGoogleSignIn();
      showMsg(
        isNewUser
          ? `Welcome to ESP for Sports, ${displayName}!`
          : `Welcome back, ${displayName}!`,
        'success'
      );
      setTimeout(() => closeModal(), 900);
    } catch (err) {
      const result = handleGoogleError(err);
      if (result.handled) {
        if (result.pendingEmail) {
          showMsg(
            'This Gmail is linked to an email/password account. Enter your password below to merge accounts.',
            'info'
          );
          setValue('email', result.pendingEmail);
        }
      } else {
        showMsg(result.message ?? 'Google sign-in failed. Please try again.', 'error');
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <form style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '8px' }} onSubmit={handleSubmit(onSubmit)} noValidate>
      {message && (
        <div style={msgStyle(message.type)} role="alert">{message.text}</div>
      )}

      {/* Email */}
      <div>
        <label style={labelStyle} htmlFor="loginEmail">Email address</label>
        <input
          id="loginEmail"
          type="email"
          autoComplete="email"
          style={inputStyle(emailFocus, !!errors.email)}
          onFocus={() => setEmailFocus(true)}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            onBlur: () => setEmailFocus(false),
          })}
        />
        {errors.email && <p style={{ fontSize: '0.78rem', color: C.error, marginTop: '6px' }}>{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label style={labelStyle} htmlFor="loginPassword">Password</label>
        <input
          id="loginPassword"
          type="password"
          autoComplete="current-password"
          style={inputStyle(pwFocus, !!errors.password)}
          onFocus={() => setPwFocus(true)}
          {...register('password', {
            required: 'Password is required',
            onBlur: () => setPwFocus(false),
          })}
        />
        {errors.password && <p style={{ fontSize: '0.78rem', color: C.error, marginTop: '6px' }}>{errors.password.message}</p>}
      </div>

      {/* Forgot */}
      <button
        type="button"
        style={{
          background: 'none', border: 'none', color: C.accent,
          fontSize: '0.82rem', cursor: 'pointer', padding: 0,
          textAlign: 'right', display: 'block', marginTop: '-8px',
          fontFamily: 'inherit',
        }}
        onClick={() => setTab('forgot')}
      >
        Forgot password?
      </button>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        style={{
          width:         '100%',
          padding:       '14px',
          borderRadius:  '999px',
          border:        'none',
          background:    `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
          color:         '#fff',
          fontSize:      '0.88rem',
          fontWeight:    700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          cursor:        isSubmitting ? 'not-allowed' : 'pointer',
          opacity:       isSubmitting ? 0.7 : 1,
          transition:    'opacity 0.2s ease, box-shadow 0.2s ease',
          boxShadow:     '0 4px 20px rgba(79,140,247,0.35)',
          fontFamily:    'inherit',
        }}
      >
        {isSubmitting ? 'Signing in…' : 'Sign In'}
      </button>

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <span style={{ color: C.muted, fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.04em' }}>or</span>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
      </div>

      {/* Google */}
      <button
        id="googleSignInBtn"
        type="button"
        disabled={isGoogleLoading}
        style={{
          width:         '100%',
          padding:       '13px',
          borderRadius:  '999px',
          background:    '#fff',
          border:        'none',
          color:         '#1a1a2e',
          fontSize:      '0.88rem',
          fontWeight:    600,
          cursor:        isGoogleLoading ? 'not-allowed' : 'pointer',
          display:       'flex',
          alignItems:    'center',
          justifyContent:'center',
          gap:           '10px',
          opacity:       isGoogleLoading ? 0.7 : 1,
          boxShadow:     '0 2px 12px rgba(0,0,0,0.3)',
          fontFamily:    'inherit',
          transition:    'opacity 0.2s ease',
        }}
        onClick={onGoogle}
      >
        <GoogleIcon />
        {isGoogleLoading ? 'Connecting…' : 'Continue with Google'}
      </button>

      {/* Switch to signup */}
      <p style={{ textAlign: 'center', color: C.muted, fontSize: '0.85rem', margin: 0 }}>
        Don't have an account?{' '}
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: C.accent, fontSize: '0.85rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
          onClick={() => setTab('signup')}
        >
          Sign up
        </button>
      </p>
    </form>
  );
}
